import { Dispatch, FC, SetStateAction } from "react";

import { Check, Translate } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

// Presentational "Translated Names" card, mirroring RubiREPORT's Manage Group
// translation UI: a count chip, a "Translate All" button, a language picker
// (with a green check on translated languages), and a per-language name field
// with an auto-translate button. All async/API logic lives in the parent.
// EXTRACT: candidate for libs/ui — see feedback/COMPONENT_PROPOSAL.md (§3.9).

export interface TranslationLanguage {
  code: string;
  name: string;
}

export interface TranslatedNamesPanelProps {
  // Source (English) text the translations derive from; gates the translate
  // buttons when empty.
  sourceText: string;
  languages: TranslationLanguage[];
  translatedNames: Record<string, string>;
  onTranslatedNamesChange: Dispatch<SetStateAction<Record<string, string>>>;
  selectedLang: string;
  onSelectedLangChange: (code: string) => void;
  translatingLang: boolean;
  translatingAll: boolean;
  onTranslateLang: () => void;
  onTranslateAll: () => void;
}

const TranslatedNamesPanel: FC<TranslatedNamesPanelProps> = ({
  sourceText,
  languages,
  translatedNames,
  onTranslatedNamesChange,
  selectedLang,
  onSelectedLangChange,
  translatingLang,
  translatingAll,
  onTranslateLang,
  onTranslateAll,
}) => {
  const translatedCount = languages.filter((lang) =>
    translatedNames[lang.code]?.trim(),
  ).length;
  const allTranslated =
    languages.length > 0 && translatedCount === languages.length;
  const nameOf = (code: string) =>
    languages.find((lang) => lang.code === code)?.name ?? code;

  const disableTranslate =
    translatingLang || translatingAll || !sourceText.trim();

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontWeight: 600 }}>Translated Names</Typography>
          <Chip
            label={`${translatedCount} / ${languages.length}`}
            color={allTranslated ? "success" : "default"}
            variant="outlined"
            size="small"
          />
        </Stack>
        <Button
          variant="outlined"
          onClick={onTranslateAll}
          disabled={disableTranslate}
          startIcon={
            translatingAll ? <CircularProgress size={16} /> : <Translate />
          }
        >
          {translatingAll ? "Translating…" : "Translate All"}
        </Button>
      </Box>

      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <TextField
          select
          label="Language"
          value={selectedLang}
          onChange={(event) => onSelectedLangChange(event.target.value)}
          fullWidth
        >
          {languages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>{lang.name}</span>
                {translatedNames[lang.code]?.trim() && (
                  <Check fontSize="small" color="success" />
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>

        {selectedLang && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={`Name in ${nameOf(selectedLang)}`}
              value={translatedNames[selectedLang] ?? ""}
              onChange={(event) =>
                onTranslatedNamesChange((prev) => ({
                  ...prev,
                  [selectedLang]: event.target.value,
                }))
              }
              fullWidth
            />
            <Tooltip title="Auto-translate from the survey name">
              <span>
                <IconButton
                  onClick={onTranslateLang}
                  disabled={disableTranslate}
                  color="primary"
                >
                  {translatingLang ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Translate />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default TranslatedNamesPanel;
